import React, { useState, useEffect } from 'react';
import EFImg from 'elem/ui/ef-img';
import EfVideo from 'elem/ui/ef-video';
import EditorLink from 'lowcode/components/EditorLink';
import { Progress } from 'antd-v5';
import styled from './index.module.scss';
import { loginUser } from '@/utils/cookie';
import Button from '@/components/UI/Button';
import EFArrowRightUp from '@/components/UI/svg/Icons/EFArrowRightUp';
import { CouponMiniIcon } from '@/components/UI/svg/Ecocredits';
import EcoCredits from '@/components/UI/svg/Icons/EcoCredits';
import { useLang } from '@/app/utils/useLang';
import getLocal from '@/app/utils/getLocal';
import { useMonthlyStore } from '@/components/EcocreditsMonthlyMadness/MonthlyProvider';
import { JoinEcoclubIcon } from '@/components/EcoCredits/Svg';
import moment from 'moment';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface props {
    openModal: (key: string) => void;
    refershData: () => void;
}

const JoinEcoclub: React.FC<props> = ({ openModal, refershData }) => {
    const { locale } = getLocal();
    const { t } = useLang('ecocredits');
    const { isLogin, rewardUserInfo, rewardUserLevel } = useMonthlyStore();
    const [href, setHref] = useState('');
    const [username, setUsername] = useState('');

    const validTimeStr = (): string => {
        if (!rewardUserLevel || rewardUserLevel.level === 1) return '';
        let format = 'DD/MM/YYYY';
        if (locale === 'kr') {
            format = 'YYYY/MM/DD';
        }
        if (locale === 'jp') {
            format = 'YYYY年MM月DD日';
        }
        if (['us', 'ca', 'eu', 'uk', 'za', 'au'].includes(locale)) {
            format = 'MM DD,YYYY';
        }
        if (locale === 'de') {
            format = 'DD.MM YYYY';
        }
        if (locale === 'it') {
            format = 'MM DD YYYY';
        }
        if (locale === 'es') {
            format = 'DD MM,YYYY';
        }
        return `(${moment(rewardUserLevel?.validStartTime).format(format)}-${moment(rewardUserLevel?.validEndTime).format(format)})`;
    };

    useEffect(() => {
        const userInfo = loginUser();
        try {
            const email = userInfo?.user.email.split('@')[0];
            const name = userInfo?.user.name;
            setUsername(name || email || '');
        } catch (e) {
            console.log(e);
        }
        setHref(window.location.href);
    }, []);

    return (
        <>
            <div className={`${styled.JoinEcoclub} ${isLogin ? 'login' : 'unlogin'}`}>
                <div className="bg">
                    <EfVideo
                        loop
                        autoPlay
                        pc="https://websiteoss.ecoflow.com/upload/video/1723799567673/PC_2560_640.mp4"
                        h5="https://websiteoss.ecoflow.com/upload/video/1723799582151/m_1024_500.mp4"
                    />
                    {/* <EFImg pc="https://websiteoss.ecoflow.com/upload/image/1721270272197/ecocredits-kv.jpg" /> */}
                </div>
                <div className="wrapper">
                    {isLogin ? (
                        <div className="content login">
                            <p className="member">
                                <span>{t('current_tier')}: </span>
                                <span>
                                    {rewardUserLevel?.name}
                                    <br />
                                    {validTimeStr()}
                                </span>
                            </p>
                            <h1 className="join-title">{t('welcome_title', { username })}</h1>
                            <p className="sub-title">{t('welcome_sub_title')}</p>
                            <p className="join-desc">{rewardUserLevel && rewardUserLevel.level < 2 ? t('welcome_text') : t('welcome_text_plus')}</p>
                        </div>
                    ) : (
                        <div className="content unlogin">
                            <h1 className="join-title">{t('join_title')}</h1>
                            <p className="join-desc">{t('join_subTitle')}</p>
                            <div className="action">
                                <EditorLink
                                    href={`/${locale}/register?redirect=${encodeURIComponent(href)}`}
                                    onClick={() => {
                                        signupCookie?.({
                                            sector_name: t('join_title'),
                                            entry_position: 1,
                                        });
                                    }}
                                    target="_self"
                                >
                                    <Button className="btn register-btn">{t('join_now')}</Button>
                                </EditorLink>
                                <EditorLink
                                    onClick={() => {
                                        signupCookie?.({
                                            sector_name: t('join_title'),
                                            entry_position: 1,
                                        });
                                    }}
                                    href={`/${locale}/login?redirect=${encodeURIComponent(href)}`}
                                    target="_self"
                                >
                                    <Button className="btn login-btn">{t('login')}</Button>
                                </EditorLink>
                            </div>
                        </div>
                    )}
                </div>

                {isLogin && (
                    <ul className="summary-content">
                        <li className="summary-content-points">
                            <p className="points-name">{t('points_balance')}</p>
                            <p className="points-value">
                                {rewardUserInfo?.points} <i>{t('points')}</i>
                            </p>
                        </li>
                        <li className="summary-content-points summary-content-freeze-points">
                            <p className="points-name">{t('coming_soon')}</p>
                            <p className="points-value disabled">
                                {rewardUserInfo?.frozenPoints} <i>{t('points')}</i>
                            </p>
                        </li>
                        <li className="summary-content-action 76">
                            <EditorLink
                                onClick={() => {
                                    openModal('coupon_modal');
                                    refershData && refershData();
                                }}
                                className="btn my_rewards"
                            >
                                {t('rewards_redeemed')}
                                {/* <EFArrowRightUp /> */}
                            </EditorLink>
                            <EditorLink
                                onClick={() => {
                                    openModal('history_modal');
                                    refershData && refershData();
                                }}
                                className="btn my_activity"
                            >
                                {t('points_activity_a')}
                                <EFArrowRightUp />
                            </EditorLink>
                        </li>
                        {rewardUserLevel && rewardUserLevel.level < 2 && (
                            <li className="summary-content-progress">
                                <Progress percent={(rewardUserInfo?.points / rewardUserLevel?.minPoint) * 100} showInfo={false} size={['100%', 4]} />
                                <span className="progress-numerical">
                                    {rewardUserInfo?.points}/{rewardUserLevel?.minPoint}
                                </span>
                                <p className="progress-tips">
                                    {t.rich('points_progress_tips', {
                                        plus: rewardUserLevel.plusName,
                                        points: rewardUserLevel?.minPoint - rewardUserInfo?.points,
                                    })}
                                </p>
                            </li>
                        )}

                        <li className="summary-content-icon">
                            <JoinEcoclubIcon />
                        </li>
                    </ul>
                )}

                {isLogin && (
                    <div className="welfare-center" onClick={() => openModal('bouns_modal')}>
                        <div className="welfare-desc">
                            <span className="welfare-text">{t('grab_title')}</span>
                            <span className="welfare-coupon-icon">
                                <CouponMiniIcon />
                            </span>
                        </div>
                        <div className="welfare-icon">
                            {/* <WelfareIcon /> */}
                            <EcoCredits />
                        </div>
                    </div>
                )}
            </div>
            <div className={`${styled.JoinHold} ${isLogin ? 'login' : 'unlogin'}`}></div>
        </>
    );
};

export default JoinEcoclub;
